/**
 * Owner: Masa Bayu
 * File: AdminAccessGate.tsx
 * Date:Time: 2026-10-23 09:35
 * Description: §467-D — gerbang peranan bagi semua laluan /admin/*. Kunci hanya
 *   satu: `GET /v1/admin/dashboard` qxp-hub menjawab 401 tanpa sesi sah dan 403
 *   untuk sesi reader/penerbit, jadi STATUS stor dashboard itu sendiri ialah
 *   keputusan gerbang — klien tidak meneka peranan dan tidak menerima token
 *   pelayan. Anak (papan pemuka) hanya dilukis selepas pelayan mengesahkan.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { getMarketplaceLocaleProfile } from '../home/marketplace/marketplace-locale';
import { useHubAuthState } from '../publication/hub-auth';
import { ADMIN_LABELS } from './admin-labels';
import { refreshAdminDashboard, resetAdminDashboard, useAdminDashboard } from './admin-dashboard';
import './admin-console.css';

function Stroke({
  children,
  size = 22,
}: {
  readonly children: ReactNode;
  readonly size?: number;
}): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function GatePanel({
  title,
  body,
  icon,
  tone,
  actions,
}: {
  readonly title: string;
  readonly body: string;
  readonly icon: ReactNode;
  readonly tone: 'info' | 'warn';
  readonly actions: readonly ReactNode[];
}): ReactElement {
  return (
    <div className="qxa qxa--gate">
      <section
        className={`qxa__gate${tone === 'warn' ? ' qxa__gate--warn' : ''}`}
        role={tone === 'warn' ? 'alert' : 'status'}
        aria-label={title}
      >
        <span className="qxa__gate-ic" aria-hidden="true">
          {icon}
        </span>
        <h1 className="qxa__gate-title">{title}</h1>
        <p className="qxa__gate-body">{body}</p>
        <div className="qxa__gate-actions">{actions}</div>
      </section>
    </div>
  );
}

export interface AdminAccessGateProps {
  readonly onNavigate: (path: string) => void;
  readonly children: ReactNode;
}

export function AdminAccessGate({ onNavigate, children }: AdminAccessGateProps): ReactElement {
  const profile = useMemo(() => getMarketplaceLocaleProfile(), []);
  const la = ADMIN_LABELS[profile.language];
  const auth = useHubAuthState();
  const dash = useAdminDashboard();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (auth !== 'signed-in') {
      resetAdminDashboard();
      return;
    }
    void refreshAdminDashboard();
  }, [auth, reloadKey]);

  const retry = useCallback((): void => {
    setReloadKey((k) => k + 1);
  }, []);

  if (auth !== 'signed-in' || dash.status === 'anonymous') {
    return (
      <GatePanel
        tone="info"
        title={la.gateSignedOutTitle}
        body={la.gateSignedOutBody}
        icon={
          <Stroke>
            <circle cx="12" cy="8" r="3.6" />
            <path d="M5 20a7 7 0 0 1 14 0" />
          </Stroke>
        }
        actions={[
          <button
            key="login"
            type="button"
            className="qxa__btn qxa__btn--primary"
            onClick={() => {
              onNavigate('/admin/login');
            }}
          >
            {la.loginCta}
          </button>,
          <button
            key="home"
            type="button"
            className="qxa__btn"
            onClick={() => {
              onNavigate('/');
            }}
          >
            {la.backHome}
          </button>,
        ]}
      />
    );
  }

  if (dash.status === 'loading') {
    return (
      <GatePanel
        tone="info"
        title={la.brand}
        body={la.gateLoading}
        icon={
          <Stroke>
            <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" />
          </Stroke>
        }
        actions={[]}
      />
    );
  }

  if (dash.status === 'forbidden') {
    return (
      <GatePanel
        tone="warn"
        title={la.gateForbiddenTitle}
        body={dash.error ?? la.gateForbiddenBody}
        icon={
          <Stroke>
            <circle cx="12" cy="12" r="9" />
            <path d="M8 12h8M12 8v8" />
          </Stroke>
        }
        actions={[
          <button
            key="home"
            type="button"
            className="qxa__btn qxa__btn--primary"
            onClick={() => {
              onNavigate('/');
            }}
          >
            {la.backHome}
          </button>,
        ]}
      />
    );
  }

  if (dash.status === 'error') {
    return (
      <GatePanel
        tone="warn"
        title={la.gateErrorTitle}
        body={dash.error ?? la.noData}
        icon={
          <Stroke>
            <path d="M12 4l9 16H3z" />
            <path d="M12 10v4M12 17h.01" />
          </Stroke>
        }
        actions={[
          <button key="retry" type="button" className="qxa__btn qxa__btn--primary" onClick={retry}>
            {la.retry}
          </button>,
        ]}
      />
    );
  }

  return <>{children}</>;
}
